import React from 'react';
import {
  Form,
  Input,
  Label,
  Modal,
  ModalBody,
  ModalHeader,
  Button,
} from 'reactstrap';
import { useForm, Controller } from 'react-hook-form';
import { mdiAccountOutline } from '@mdi/js';
import Icon from '@mdi/react';
import { yupResolver } from '@hookform/resolvers/yup';
import { initialValues, validationSchema } from './constants';
import { UserAsyncAutocompleteSelect } from '../../Common';
import { fetchHRCodeList } from '../../../store/apis/commonService';
import { getAuthUser } from '../../../utils/auth.util';

import './ReassignTicket.scss';

function ReassignTicket({ isOpen, toggle, handleOnSubmit, isLoading }) {
  const {
    control,
    handleSubmit,
    reset,
    formState: { errors, isValid },
    watch,
    setValue,
    trigger,
  } = useForm({
    defaultValues: initialValues,
    resolver: yupResolver(validationSchema),
    mode: 'onChange',
  });

  const onSubmit = values => {
    const payload = {
      reassignTo: values.person?.value?.trim(),
      reassignReason: values.reason,
    };
    handleOnSubmit(payload, reset);
  };

  const watchValues = watch();

  return (
    <Modal isOpen={isOpen} toggle={toggle} centered size="md">
      <ModalHeader
        className="reassign-modal-header pb-3"
        toggle={() => {
          toggle();
          reset();
        }}
      >
        <div className="fw-semibold fs-5">Re-Assign</div>
      </ModalHeader>
      <ModalBody>
        <Form
          onSubmit={handleSubmit(onSubmit)}
          className="d-flex flex-column gap-3"
        >
          <div>
            <Label>
              Select Person<span className="text-danger">*</span>
            </Label>
            <Controller
              control={control}
              name="person"
              render={() => (
                <UserAsyncAutocompleteSelect
                  autoCompleteFn={() =>
                    fetchHRCodeList({
                      type: 'TICKET_OWNER',
                      param1: getAuthUser()?.hrCoCd.trim(),
                    })
                  }
                  mappedFn={rawData =>
                    rawData.map(item => ({
                      value: item.Code,
                      label: item.Description,
                      name: item.Description,
                      code: item.Code,
                      designation: item.Designation,
                      image: item?.ImageFile,
                      coCd: item?.CoCd?.trim(),
                    }))
                  }
                  onChange={e => {
                    setValue('person', e);
                    trigger('person');
                  }}
                  placeholder="Search by Employee Name & ID"
                  isClearable
                  value={watchValues.person}
                  className={errors.person ? 'is-invalid' : ''}
                  styles={{
                    control: (provided, state) => {
                      let { borderColor } = provided;
                      if (errors.person) {
                        borderColor = 'red';
                      } else if (state.isFocused) {
                        borderColor = provided.borderColor;
                      }
                      return {
                        ...provided,
                        borderColor,
                        '&:hover': {
                          borderColor,
                        },
                        '&:focus': {
                          borderColor,
                        },
                        boxShadow: errors.person ? 'none' : provided.boxShadow,
                      };
                    },
                  }}
                  inputIcon={
                    <Icon path={mdiAccountOutline} size={0.8} color="#667085" />
                  }
                  defaultShowMenu
                  rightKeyAssessor="code"
                />
              )}
            />
            {errors.person && (
              <div className="invalid-feedback">{errors.person.message}</div>
            )}
          </div>
          <div>
            <Label>
              Reason<span className="text-danger">*</span>
            </Label>
            <Controller
              control={control}
              name="reason"
              render={({ field }) => (
                <Input
                  {...field}
                  type="textarea"
                  className={`external-filter-input form-control ${errors.reason ? 'is-invalid' : ''}`}
                  placeholder="Enter Reason"
                  rows="4"
                />
              )}
            />
            {errors.reason && (
              <div className="invalid-feedback">{errors.reason.message}</div>
            )}
          </div>
          <div className="d-flex justify-content-center">
            <Button
              className="w-75 custom-btn"
              type="submit"
              disabled={!isValid || isLoading}
            >
              {isLoading ? 'Reassigning...' : 'Reassign'}
            </Button>
          </div>
        </Form>
      </ModalBody>
    </Modal>
  );
}

export default ReassignTicket;
